import { createReducer } from '@reduxjs/toolkit';

import {
  AdjustmentSortsType,
  OrderRealizationDetailedStateType,
  SortsType,
} from '../../types';
import {
  fetchAdjustmentOrders,
  fetchRealizationPositions,
  getIssueDoc,
  initAdjustmentDoc,
  initIssue,
  initIssuePayment,
  setAdjustmentSort,
  setError,
  setRealizationPositionsLoading,
  setSignedDecisionDoc,
  setSigningInfo,
  setSigningStatus,
  setSigninIds,
  setSort,
  signAdjustmentDoc,
  signDecision,
} from '../actions/order-realization-detailed';

const initialState: OrderRealizationDetailedStateType = {
  data: {
    adjustmentPositions: [],
    info: {},
    docs: [],
    signingIds: [],
    decisionDoc: undefined,
    sorts: {
      positions: {
        direction: 'ASC',
        active: false,
      },
      count: {
        direction: 'ASC',
        active: false,
      },
      sum: {
        direction: 'ASC',
        active: false,
      },
    },
    adjustmentSorts: {
      oldCount: {
        direction: 'ASC',
        active: false,
      },
      count: {
        direction: 'ASC',
        active: false,
      },
      oldSum: {
        direction: 'ASC',
        active: false,
      },
      sum: {
        direction: 'ASC',
        active: false,
      },
    },
  },
  error: {
    signing: false,
  },
  loading: {
    positions: false,
    docs: false,
    decisionDoc: false,
    signing: false,
    signingAdjustment: false,
  },
};

const orderRealizationDetailedReducer = createReducer(
  initialState,
  (builder) => {
    builder.addCase(setRealizationPositionsLoading, (state, action) => {
      state.loading[action.payload.type] = action.payload.value;
    });
    builder.addCase(fetchRealizationPositions.fulfilled, (state, action) => {
      if (action.payload) {
        state.data.info.itemId = action.payload.id;
        state.data.info.itemStatus = action.payload.status;
        state.data.info.positions = action.payload.positions;
        state.data.info.refNumber = action.payload.name;
        state.data.info.sum = action.payload.price;
        state.data.info.tokenName = action.payload.tokenName;
        state.data.info.comment = action.payload.comment;
        state.data.info.issuingInfoId = action.payload.issuingInfoId;
        state.data.info.tokenId = action.payload.tokenId;
      }

      if (
        action.payload?.adjustments &&
        action.payload.adjustments.length > 0
      ) {
        state.data.adjustment = { ...action.payload.adjustments[0] };
      }
    });
    builder.addCase(initIssue.fulfilled, (state, action) => {
      if (action.payload) {
        state.data.decisionId = action.payload.id;
      }
    });
    builder.addCase(initIssuePayment.fulfilled, (state, action) => {
      if (action.payload) {
        state.data.decisionId = action.payload.id;
      }
    });
    builder.addCase(setSigninIds, (state, action) => {
      state.data.signingIds = action.payload;
    });
    builder.addCase(getIssueDoc.fulfilled, (state, action) => {
      state.data.decisionDoc = action.payload;
    });
    builder.addCase(setAdjustmentSort, (state, action) => {
      state.data.adjustmentSorts[action.payload.name].direction =
        action.payload.direction;
      state.data.adjustmentSorts[action.payload.name].active = true;

      const adjustmentSortsKeys = Object.keys(state.data.adjustmentSorts);
      adjustmentSortsKeys.forEach((key) => {
        if (key !== action.payload.name) {
          type keyUnionType = keyof AdjustmentSortsType;
          state.data.adjustmentSorts[key as keyUnionType] = {
            active: false,
            direction: 'ASC',
          };
        }
      });
    });
    builder.addCase(setSort, (state, action) => {
      state.data.sorts[action.payload.name].direction =
        action.payload.direction;
      state.data.sorts[action.payload.name].active = true;

      const sortsKeys = Object.keys(state.data.sorts);
      sortsKeys.forEach((key) => {
        if (key !== action.payload.name) {
          type keyUnionType = keyof SortsType;
          state.data.sorts[key as keyUnionType] = {
            active: false,
            direction: 'ASC',
          };
        }
      });
    });
    builder.addCase(setSignedDecisionDoc, (state, action) => {
      state.data.signedDecisionDoc = action.payload;
    });
    builder.addCase(setSigningInfo, (state, action) => {
      state.data.signingInfo = action.payload;
    });
    builder.addCase(signDecision.fulfilled, (state, action) => {
      if (action.payload) {
        state.data.signingStatus = action.payload ? 'success' : 'rejected';
      }
    });
    builder.addCase(signAdjustmentDoc.fulfilled, (state, action) => {
      if (action.payload) {
        state.data.signingStatus = action.payload ? 'success' : 'rejected';
      }
    });
    builder.addCase(setError, (state, action) => {
      state.error[action.payload.name] = action.payload.value;
    });
    builder.addCase(setSigningStatus, (state, action) => {
      state.data.signingStatus = action.payload;
    });
    builder.addCase(fetchAdjustmentOrders.fulfilled, (state, action) => {
      state.data.adjustmentPositions = action.payload;
    });
    builder.addCase(initAdjustmentDoc.fulfilled, (state, action) => {
      state.data.adjustmentDoc = action.payload;
    });
  }
);

export default orderRealizationDetailedReducer;
